import { useAuthenticator } from "@aws-amplify/ui-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Lock } from "lucide-react";
import { resolveOpenFinancialAidCycle } from "@triedge/staff-settings-domain";
import { AppShell } from "../../components/app-shell/AppShell";
import { AdmissionsAIAssistant } from "../../components/ai/AdmissionsAIAssistant";
import type { AdmissionsRouteAssistantContext } from "../../components/ai/types";
import {
  PARENT_PORTAL_NAV_ITEMS,
  PARENT_PORTAL_ROUTE_MAP,
  PARENT_PORTAL_SETTINGS_ITEM,
  ParentAppShell,
  resolveParentPortalNavId,
  type ParentPortalNavId,
} from "../../components/app-shell/ParentAppShell";
import {
  resolveModuleFromPath,
  SETTINGS_MODULE,
  SHELL_MODULES,
  type ShellPersona,
} from "../../app-shell/moduleRegistry";
import { resolveParentShellContentInsetProfile } from "../../app-shell/parentShellInsetProfiles";
import {
  resolveStaffSidebarNavigationState,
  type StaffShellNavId,
} from "../../app-shell/sidebar-navigation";
import {
  PARENT_TEMP_NAV_ITEMS,
  PARENT_TEMP_ROUTE_MAP,
  resolveParentTempNavId,
  resolveStaffTempNavId,
  STAFF_TEMP_NAV_ITEMS,
  STAFF_TEMP_ROUTE_MAP,
  TEMP_NAV_LABEL,
} from "../../app-shell/tempNavigation";
import { AdmissionsSemanticRoute } from "../../semantic-routing/admissions/AdmissionsSemanticRoute";
import { FinancialAidSemanticRoute } from "../../semantic-routing/financial-aid/FinancialAidSemanticRoute";
import { FacultySemanticRoute } from "../../semantic-routing/faculty/FacultySemanticRoute";
import { ParentPortalSemanticRoute } from "../../semantic-routing/parent-portal/ParentPortalSemanticRoute";
import { SettingsSemanticRoute } from "../../semantic-routing/settings/SettingsSemanticRoute";
import { StudentsFamiliesSemanticRoute } from "../../semantic-routing/students-families/StudentsFamiliesSemanticRoute";
import { AttendanceSemanticRoute } from "../../semantic-routing/attendance/AttendanceSemanticRoute";
import { DateInputEnhancerContractPage } from "../../internal-owned/school-settings-v2/DateInputEnhancerContractPage";
import { RunReportsRoute } from "../../report-generation/RunReportsRoute";
import {
  useFinancialAidByApplicant,
  useFinancialAidOpenCycle,
} from "../../financial-aid/hooks/useFinancialAidApi";
import { SchoolCampsSourceFaithfulRoutes } from "../../internal-owned/source-faithful/school-camps";
import { ParentCampsSourceFaithfulRoutes } from "../../internal-owned/source-faithful/parent-camps";
import { ParentParnesHayomSourceFaithfulRoutes } from "../../internal-owned/source-faithful/parent-parnes-hayom";
import { SchoolDonationsSourceFaithfulRoutes } from "../../internal-owned/source-faithful/school-donations";
import { SchoolTipATeacherSourceFaithfulRoutes } from "../../internal-owned/source-faithful/school-tip-a-teacher";
import { AcademicsSourceFaithfulRoutes } from "../../internal-owned/source-faithful/school-academics";
import { FinancialOpsSourceFaithfulRoutes } from "../../internal-owned/source-faithful/school-finance";
import { AdministratorTuitionInternalRoutes } from "../../internal-owned/administrator-tuition";
import { SubstituteSourceFaithfulRoutes } from "../../internal-owned/source-faithful/school-substitutes";
import { SchoolPayrollSourceFaithfulRoutes } from "../../internal-owned/source-faithful/school-payroll";
import { ParentDonationsSourceFaithfulRoutes } from "../../internal-owned/source-faithful/parent-donations";
import { ParentTipATeacherSourceFaithfulRoutes } from "../../internal-owned/source-faithful/parent-tip-a-teacher";
import { ParentAcademicPerformanceSourceFaithfulRoutes } from "../../internal-owned/source-faithful/parent-academic-performance";
import { SchoolTuitionOwnedRoutes } from "../../internal-owned/tuition/school";
import { ParentTuitionOwnedRoutes } from "../../internal-owned/tuition/parent";
import { SuperAdminControlPlaneRoutes } from "../../internal-owned/super-admin-control-plane/SuperAdminControlPlaneRoutes";
import {
  useGetApplicantApplicationsQuery,
  useGetSettingsModuleConfigQuery,
} from "../../state/domainRouterApi";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { useShellProfile } from "../../state/useShellProfile";
import {
  materializeModuleGovernanceState,
  resolveGovernedParentPortalFallbackRoute,
  resolveEnabledParentTempNavIds,
  resolveEnabledParentSidebarNavIds,
  resolveEnabledSchoolTempNavIds,
  resolveEnabledSchoolSidebarNavIds,
  resolveGovernedRouteDisableReason,
} from "../../platform-governance/moduleGovernanceCatalog";
import {
  resetShellState,
  setAssistantOpen,
  setAssistantWidthMode,
  setDefaultModule,
  setLastVisitedPath,
  setNavOpen,
  setPersonaOverride,
} from "../../state/shellSlice";
import { clearShellStorage } from "../../state/shellStorage";
import { getShellHeaderPolicy } from "../shellHeaderPolicy";
import {
  TRIEDGE_OVERRIDE_LABELS,
  emitMinimumOverrideModeBootLog,
  isMinimumOverrideModeEnabled,
} from "../../app-shell/overrideGovernance";
import {
  canonicalizeParentPortalDefaultPath,
  isModuleId,
  isParentRoutePath,
  isSuperAdministratorRoutePath,
  isSelfGuardedSchoolRoutePath,
  normalizeShellPath,
  resolveAllowedModules,
  resolvePersonaDefaultEntry,
  resolvePostAuthTarget,
  resolveShellRouteViolation,
} from "../routePolicy";
import {
  beginLogoutTransition,
  clearLocalAuthState,
  POST_LOGOUT_PUBLIC_PATH,
} from "../../auth/logoutTransition";
import { isParentFinancialAidEntryFocusActive } from "../../financial-aid/parentFinancialAidStorage";
import {
  AdminImpersonationBannerLive,
  AdminViewAsBannerLive,
  SuperAdminImpersonationWidgetLive,
} from "../../components/app-shell/AdminChrome";
import {
  useAdminChrome,
  type LaunchImpersonationSessionInput,
} from "../../state/useAdminChrome";
import { parseSettingsState as parseNormalizedSettingsState } from "../../settings/settingsState";
import {
  resolveParentFinancialAidFocusedNavIds,
  resolveParentFinancialAidAccessGateState,
  shouldFocusParentShellOnFinancialAid,
} from "../../shared/financialAidAccess";
import { useLogRocketIdentity } from "../../telemetry/useLogRocketIdentity";
import {
  resolveEffectiveParentPortalNavIds,
  resolveParentFinancialAidFocusedNavOptions,
  resolveParentPortalModuleGateState,
} from "../../parent-portal/parentPortalModuleAvailability";

const MOBILE_SCHOOL_ASSISTANT_BREAKPOINT_PX = 1024;
const SETTINGS_MODULE_KEY = "staff_settings";

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const parseSettingsPayload = (rawPayload: unknown) =>
  parseNormalizedSettingsState(rawPayload);

export const ParentPortalAliasRoute = () => {
  const location = useLocation();
  const canonicalPath = canonicalizeParentPortalDefaultPath(location.pathname);
  const target = `${canonicalPath}${location.search}${location.hash}`;
  if (target !== `${location.pathname}${location.search}${location.hash}`) {
    return <Navigate to={target} replace />;
  }
  return <ParentPortalSemanticRoute />;
};

const TempAliasRedirectRoute = ({
  fromPrefix,
  toPrefix,
}: {
  fromPrefix: string;
  toPrefix: string;
}) => {
  const location = useLocation();
  const normalizedFrom = fromPrefix.replace(/\/+$/, "");
  const normalizedTo = toPrefix.replace(/\/+$/, "");
  const suffix = location.pathname.startsWith(normalizedFrom)
    ? location.pathname.slice(normalizedFrom.length)
    : "";
  const targetPath = `${normalizedTo}${suffix}` || normalizedTo || "/";
  return (
    <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />
  );
};

export const ShellLayoutRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    authStatus,
    basePersona,
    effectivePersona,
    profile,
    staffAccessPendingApproval,
  } = useShellProfile();
  const shellState = useAppSelector((state) => state.shell);
  const { signOut } = useAuthenticator((context) => [context.signOut]);
  const [
    parentFinancialAidEntryFocusActive,
    setParentFinancialAidEntryFocusActive,
  ] = useState(() => isParentFinancialAidEntryFocusActive());
  useLogRocketIdentity({
    profile,
    effectivePersona,
    basePersona,
    authStatus,
  });
  const settingsConfigQuery = useGetSettingsModuleConfigQuery({
    moduleKey: SETTINGS_MODULE_KEY,
  });
  const { refetch: refetchSettingsConfig } = settingsConfigQuery;
  const settingsConfigRequestPending = Boolean(
    settingsConfigQuery.isLoading ||
      (settingsConfigQuery.isFetching && !settingsConfigQuery.data),
  );
  const canonicalPath = normalizeShellPath(location.pathname);
  const isParentRoute = isParentRoutePath(canonicalPath);
  const [settingsConfigTimedOut, setSettingsConfigTimedOut] = useState(false);
  useEffect(() => {
    if (
      !isParentRoute ||
      !settingsConfigRequestPending ||
      settingsConfigQuery.data ||
      settingsConfigQuery.isError
    ) {
      setSettingsConfigTimedOut(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(
      () => setSettingsConfigTimedOut(true),
      8000,
    );
    return () => window.clearTimeout(timeoutId);
  }, [
    isParentRoute,
    settingsConfigQuery.data,
    settingsConfigQuery.isError,
    settingsConfigRequestPending,
  ]);
  const settingsConfigPending = Boolean(
    settingsConfigRequestPending &&
      !settingsConfigTimedOut &&
      !settingsConfigQuery.isError,
  );
  const settingsConfigFailed = Boolean(
    isParentRoute &&
      !settingsConfigQuery.data &&
      (settingsConfigQuery.isError || settingsConfigTimedOut),
  );
  const retryParentSettings = useCallback(() => {
    setSettingsConfigTimedOut(false);
    void refetchSettingsConfig();
  }, [refetchSettingsConfig]);
  const parentApplicantId = profile?.userSub?.trim() ?? "";
  const parentAdmissionsFinancialAidAccessQuery =
    useGetApplicantApplicationsQuery(
      { applicantId: parentApplicantId, limit: 50 },
      { skip: !isParentRoute || !parentApplicantId },
    );
  const parentFinancialAidApplicationsQuery = useFinancialAidByApplicant(
    parentApplicantId,
    Boolean(isParentRoute && parentApplicantId),
  );
  const parentFinancialAidOpenCycleQuery = useFinancialAidOpenCycle(
    undefined,
    Boolean(isParentRoute),
  );
  const parentFinancialAidOpenCycleRecord = asRecord(
    parentFinancialAidOpenCycleQuery.data,
  );
  const parentFinancialAidOpenCycleEnabled = Boolean(
    isParentRoute && parentFinancialAidOpenCycleRecord.isOpen === true,
  );
  const parentFinancialAidOpenCycleLookupPending = Boolean(
    isParentRoute &&
      parentFinancialAidOpenCycleQuery.isLoading &&
      !parentFinancialAidOpenCycleQuery.data &&
      !parentFinancialAidOpenCycleQuery.error,
  );
  const parentFinancialAidAccessDiscoveryPending = Boolean(
    isParentRoute &&
      parentApplicantId &&
      (parentAdmissionsFinancialAidAccessQuery.isLoading ||
        (parentAdmissionsFinancialAidAccessQuery.isFetching &&
          !parentAdmissionsFinancialAidAccessQuery.data) ||
        parentFinancialAidApplicationsQuery.isLoading ||
        parentFinancialAidOpenCycleLookupPending),
  );
  // Record lookups must never trap parents at the access gate: if they have
  // not settled within the watchdog window, the gate evaluates with whatever
  // has loaded (an open application cycle already grants access on its own).
  const [
    parentFinancialAidLookupTimedOut,
    setParentFinancialAidLookupTimedOut,
  ] = useState(false);
  useEffect(() => {
    if (!parentFinancialAidAccessDiscoveryPending) {
      setParentFinancialAidLookupTimedOut(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(
      () => setParentFinancialAidLookupTimedOut(true),
      8000,
    );
    return () => window.clearTimeout(timeoutId);
  }, [parentFinancialAidAccessDiscoveryPending]);
  const parentFinancialAidCycleOpen = useMemo(() => {
    if (!isParentRoute) return false;
    const payload = asRecord(settingsConfigQuery.data).payload;
    if (payload == null) return false;
    const parsed = parseSettingsPayload(payload);
    return resolveOpenFinancialAidCycle(parsed.financialAid)?.isOpen === true;
  }, [isParentRoute, settingsConfigQuery.data]);
  const parentFinancialAidAccessGateState = useMemo(
    () =>
      resolveParentFinancialAidAccessGateState({
        canonicalPath,
        search: location.search,
        parentApplicantId,
        admissionsApplications:
          parentAdmissionsFinancialAidAccessQuery.data?.items ?? [],
        admissionsLookupPending: Boolean(
          !parentFinancialAidLookupTimedOut &&
            (parentAdmissionsFinancialAidAccessQuery.isLoading ||
              (parentAdmissionsFinancialAidAccessQuery.isFetching &&
                !parentAdmissionsFinancialAidAccessQuery.data)),
        ),
        financialAidApplications:
          parentFinancialAidApplicationsQuery.data?.items ?? [],
        financialAidLookupPending: Boolean(
          !parentFinancialAidLookupTimedOut &&
            parentFinancialAidApplicationsQuery.isLoading,
        ),
        cycleOpen: parentFinancialAidCycleOpen,
        openCycleEntryEnabled: parentFinancialAidOpenCycleEnabled,
        openCycleLookupPending: parentFinancialAidOpenCycleLookupPending,
      }),
    [
      canonicalPath,
      location.search,
      parentAdmissionsFinancialAidAccessQuery.data,
      parentAdmissionsFinancialAidAccessQuery.isFetching,
      parentAdmissionsFinancialAidAccessQuery.isLoading,
      parentApplicantId,
      parentFinancialAidApplicationsQuery.data,
      parentFinancialAidApplicationsQuery.isLoading,
      parentFinancialAidCycleOpen,
      parentFinancialAidLookupTimedOut,
      parentFinancialAidOpenCycleEnabled,
      parentFinancialAidOpenCycleLookupPending,
    ],
  );
  const parentFinancialAidAccessLookupPending =
    parentFinancialAidAccessGateState.lookupPending;
  const parentFinancialAidAccessEnabled =
    parentFinancialAidAccessGateState.accessEnabled;
  const hasParentFinancialAidInviteEntry =
    parentFinancialAidAccessGateState.hasInviteEntry;
  const parentFinancialAidFocusedPortalActive =
    shouldFocusParentShellOnFinancialAid({
      isParentRoute,
      entryFocusActive: parentFinancialAidEntryFocusActive,
      inviteEntryActive: hasParentFinancialAidInviteEntry,
      cycleOpen: parentFinancialAidCycleOpen,
      openCycleEntryEnabled: parentFinancialAidOpenCycleEnabled,
      openCycleLookupPending: parentFinancialAidOpenCycleLookupPending,
    });
  const parentFinancialAidAccessGateActive =
    parentFinancialAidAccessGateState.gateActive;
  const settingsState = useMemo(() => {
    const payload = asRecord(settingsConfigQuery.data).payload;
    return parseSettingsPayload(payload);
  }, [settingsConfigQuery.data]);
  const governanceState = useMemo(
    () => materializeModuleGovernanceState(settingsState.governance),
    [settingsState.governance],
  );
  const enabledSchoolNavIds = useMemo(
    () => resolveEnabledSchoolSidebarNavIds(governanceState),
    [governanceState],
  );
  const enabledParentNavIds = useMemo(
    () => resolveEnabledParentSidebarNavIds(governanceState),
    [governanceState],
  );
  const parentFinancialAidFocusedNavOptions = useMemo(
    () =>
      resolveParentFinancialAidFocusedNavOptions(
        enabledParentNavIds,
        parentFinancialAidFocusedPortalActive,
      ),
    [enabledParentNavIds, parentFinancialAidFocusedPortalActive],
  );
  const effectiveEnabledParentNavIds = useMemo(() => {
    const next = new Set(enabledParentNavIds);
    if (
      !parentFinancialAidEntryFocusActive &&
      !parentFinancialAidAccessEnabled &&
      !parentFinancialAidAccessDiscoveryPending &&
      !hasParentFinancialAidInviteEntry
    ) {
      next.delete("financial-aid");
    }
    return resolveParentFinancialAidFocusedNavIds(
      next,
      parentFinancialAidFocusedPortalActive,
      parentFinancialAidFocusedNavOptions,
    );
  }, [
    enabledParentNavIds,
    hasParentFinancialAidInviteEntry,
    parentFinancialAidFocusedNavOptions,
    parentFinancialAidEntryFocusActive,
    parentFinancialAidFocusedPortalActive,
    parentFinancialAidAccessDiscoveryPending,
    parentFinancialAidAccessEnabled,
  ]);
  const visibleParentNavIds = useMemo(
    () => resolveEffectiveParentPortalNavIds(effectiveEnabledParentNavIds),
    [effectiveEnabledParentNavIds],
  );
  const parentModuleGateState = useMemo(
    () =>
      resolveParentPortalModuleGateState({
        pathname: canonicalPath,
        visibleNavIds: visibleParentNavIds,
        settingsPending: settingsConfigPending,
      }),
    [canonicalPath, settingsConfigPending, visibleParentNavIds],
  );
  const parentModuleRouteGuardPending =
    parentModuleGateState.status === "pending";
  const parentModuleRouteGuardDisabled =
    parentModuleGateState.status === "disabled";
  const enabledSchoolTempNavIds = useMemo(
    () => resolveEnabledSchoolTempNavIds(governanceState),
    [governanceState],
  );
  const enabledParentTempNavIds = useMemo(
    () => resolveEnabledParentTempNavIds(governanceState),
    [governanceState],
  );
  const routeNormalizationTarget = useMemo(() => {
    if (canonicalPath === location.pathname) {
      return null;
    }
    return `${canonicalPath}${location.search}${location.hash}`;
  }, [canonicalPath, location.hash, location.pathname, location.search]);
  const hasEnabledParentModules = visibleParentNavIds.size > 0;
  const zeroEnabledParentModules = isParentRoute && !hasEnabledParentModules;
  const minimumOverrideModeEnabled = isMinimumOverrideModeEnabled();
  const disabledVisibleParentModuleMessage = useMemo(() => {
    if (!isParentRoute || parentModuleGateState.status !== "disabled") {
      return null;
    }
    if (!parentModuleGateState.moduleId) {
      return null;
    }
    return `Parent portal module "${parentModuleGateState.moduleId}" is not enabled.`;
  }, [isParentRoute, parentModuleGateState]);
  const governanceRouteViolationMessage = useMemo(
    () =>
      settingsConfigPending
        ? null
        : resolveGovernedRouteDisableReason(canonicalPath, governanceState),
    [canonicalPath, governanceState, settingsConfigPending],
  );
  const shellRouteViolationMessage = useMemo(() => {
    if (routeNormalizationTarget) {
      return null;
    }
    return resolveShellRouteViolation(
      canonicalPath,
      effectivePersona,
      profile?.groups,
    );
  }, [
    canonicalPath,
    effectivePersona,
    profile?.groups,
    routeNormalizationTarget,
  ]);
  const routeViolationMessage = useMemo(() => {
    if (routeNormalizationTarget) {
      return null;
    }
    if (shellRouteViolationMessage) {
      return shellRouteViolationMessage;
    }
    if (zeroEnabledParentModules) {
      return null;
    }
    if (disabledVisibleParentModuleMessage) {
      return disabledVisibleParentModuleMessage;
    }
    if (hasParentFinancialAidInviteEntry) {
      return governanceRouteViolationMessage;
    }
    return governanceRouteViolationMessage;
  }, [
    disabledVisibleParentModuleMessage,
    governanceRouteViolationMessage,
    hasParentFinancialAidInviteEntry,
    routeNormalizationTarget,
    shellRouteViolationMessage,
    zeroEnabledParentModules,
  ]);
  const isSchoolRoute =
    canonicalPath.startsWith("/school/") ||
    canonicalPath.startsWith("/administrator/") ||
    canonicalPath.startsWith("/super-administrator/");
  const isAssistantFullPageRoute =
    canonicalPath === "/school/assistant" ||
    canonicalPath.startsWith("/school/assistant/");
  const isRunReportsRoute =
    canonicalPath === "/school/run-reports" ||
    canonicalPath.startsWith("/school/run-reports/");
  const [isMobileSchoolViewport, setIsMobileSchoolViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(
      `(max-width: ${MOBILE_SCHOOL_ASSISTANT_BREAKPOINT_PX}px)`,
    ).matches;
  });
  const mobileAssistantNormalizedPathsRef = useRef<Set<string>>(new Set());
  const shouldDefaultAssistantClosedOnMobileSchool =
    isSchoolRoute && isMobileSchoolViewport;
  const lockAssistantOpen = false;
  const resolvedAssistantOpen = shellState.assistantOpen;
  const resolvedAssistantWidthMode = shellState.assistantWidthMode;
  const [assistantRouteContext, setAssistantRouteContext] =
    useState<AdmissionsRouteAssistantContext | null>(null);
  const activeAssistantRouteContext = useMemo(
    () =>
      assistantRouteContext?.pathname === location.pathname
        ? assistantRouteContext
        : null,
    [assistantRouteContext, location.pathname],
  );
  const defaultRoute = useMemo(() => {
    const parentDefaultRoute =
      resolveGovernedParentPortalFallbackRoute(visibleParentNavIds);
    const candidate = resolvePostAuthTarget({
      persona: effectivePersona,
      lastVisitedPath: shellState.lastVisitedPath,
      defaultModule: shellState.defaultModule,
      enabledModuleIds: enabledSchoolNavIds,
      authGroups: profile?.groups,
      parentDefaultPath: parentDefaultRoute,
    });
    if (
      !resolveGovernedRouteDisableReason(
        normalizeShellPath(candidate),
        governanceState,
      )
    ) {
      return candidate;
    }
    if (isParentRoute) {
      return parentDefaultRoute ?? PARENT_PORTAL_ROUTE_MAP.dashboard;
    }
    return resolvePersonaDefaultEntry(
      effectivePersona,
      shellState.defaultModule,
      enabledSchoolNavIds,
      profile?.groups,
      parentDefaultRoute,
    );
  }, [
    effectivePersona,
    enabledSchoolNavIds,
    governanceState,
    isParentRoute,
    profile?.groups,
    shellState.defaultModule,
    shellState.lastVisitedPath,
    visibleParentNavIds,
  ]);

  useEffect(() => {
    if (!isParentRoute) {
      return;
    }
    setParentFinancialAidEntryFocusActive(
      isParentFinancialAidEntryFocusActive(),
    );
  }, [isParentRoute, location.pathname, location.search]);

  useEffect(() => {
    emitMinimumOverrideModeBootLog("composed-shell");
  }, []);

  useEffect(() => {
    if (!routeViolationMessage) return;
    const currentPath = `${location.pathname}${location.search}`;
    if (currentPath === defaultRoute) {
      return;
    }
    console.error(`${routeViolationMessage} Redirecting to "${defaultRoute}".`);
    navigate(defaultRoute, { replace: true });
  }, [
    defaultRoute,
    location.pathname,
    location.search,
    navigate,
    routeViolationMessage,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const moduleParam = params.get("defaultModule");
    if (isModuleId(moduleParam)) {
      dispatch(setDefaultModule(moduleParam));
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    if (
      canonicalPath === "/" ||
      canonicalPath.startsWith("/auth") ||
      isAssistantFullPageRoute
    ) {
      return;
    }
    dispatch(setLastVisitedPath(`${canonicalPath}${location.search}`));
  }, [canonicalPath, dispatch, isAssistantFullPageRoute, location.search]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => {};
    }
    const mediaQuery = window.matchMedia(
      `(max-width: ${MOBILE_SCHOOL_ASSISTANT_BREAKPOINT_PX}px)`,
    );
    const handleMediaChange = (event: MediaQueryListEvent) => {
      setIsMobileSchoolViewport(event.matches);
    };
    setIsMobileSchoolViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaChange);
      return () => mediaQuery.removeEventListener("change", handleMediaChange);
    }
    mediaQuery.addListener(handleMediaChange);
    return () => mediaQuery.removeListener(handleMediaChange);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.body.dataset.triedgeMinimumOverrideMode =
      minimumOverrideModeEnabled ? "1" : "0";
    document.body.dataset.triedgeOverrideForceAssistantCollapsed =
      "removed-by-policy";
    document.body.dataset.triedgeOverrideForceAssistantCollapsedLabel =
      TRIEDGE_OVERRIDE_LABELS.forceAssistantCollapsed;
    document.body.dataset.triedgeOverrideStripCrossHeader = "removed-by-policy";
    document.body.dataset.triedgeOverrideStripCrossHeaderLabel =
      TRIEDGE_OVERRIDE_LABELS.stripCrossHeader;
    return () => {
      delete document.body.dataset.triedgeMinimumOverrideMode;
      delete document.body.dataset.triedgeOverrideForceAssistantCollapsed;
      delete document.body.dataset.triedgeOverrideForceAssistantCollapsedLabel;
      delete document.body.dataset.triedgeOverrideStripCrossHeader;
      delete document.body.dataset.triedgeOverrideStripCrossHeaderLabel;
    };
  }, [minimumOverrideModeEnabled]);

  useEffect(() => {
    if (!shouldDefaultAssistantClosedOnMobileSchool) {
      return;
    }
    if (mobileAssistantNormalizedPathsRef.current.has(canonicalPath)) {
      return;
    }
    mobileAssistantNormalizedPathsRef.current.add(canonicalPath);
    if (shellState.assistantOpen) {
      dispatch(setAssistantOpen(false));
    }
  }, [
    canonicalPath,
    dispatch,
    shellState.assistantOpen,
    shouldDefaultAssistantClosedOnMobileSchool,
  ]);

  const allowedModules = useMemo(
    () =>
      resolveAllowedModules(
        effectivePersona,
        enabledSchoolNavIds,
        profile?.groups,
      ),
    [effectivePersona, enabledSchoolNavIds, profile?.groups],
  );
  const showSettings = useMemo(
    () =>
      SETTINGS_MODULE.allowedPersonas.includes(
        effectivePersona as ShellPersona,
      ),
    [effectivePersona],
  );
  const authorizedSchoolNavIds = useMemo(() => {
    const next = new Set(allowedModules.map((module) => module.id));
    next.add("assistant");
    if (showSettings && enabledSchoolNavIds.has(SETTINGS_MODULE.id)) {
      next.add(SETTINGS_MODULE.id);
    }
    return next;
  }, [allowedModules, enabledSchoolNavIds, showSettings]);
  const defaultModuleId = "admissions";
  const defaultModule = useMemo(() => {
    const preferred =
      allowedModules.find((module) => module.id === defaultModuleId) ??
      allowedModules[0];
    if (preferred) return preferred;
    if (
      SETTINGS_MODULE.allowedPersonas.includes(effectivePersona as ShellPersona)
    ) {
      return SETTINGS_MODULE;
    }
    return SHELL_MODULES[0] ?? SETTINGS_MODULE;
  }, [allowedModules, defaultModuleId, effectivePersona]);
  const staffSidebarNavigation = useMemo(
    () =>
      resolveStaffSidebarNavigationState({
        persona: effectivePersona,
        activePath: canonicalPath,
        enabledNavIds: authorizedSchoolNavIds,
        includeSettings: showSettings,
      }),
    [authorizedSchoolNavIds, canonicalPath, effectivePersona, showSettings],
  );
  const parentNavItems = useMemo(
    () =>
      PARENT_PORTAL_NAV_ITEMS.filter((item) =>
        visibleParentNavIds.has(String(item.id)),
      ),
    [visibleParentNavIds],
  );
  const enabledStaffTempItems = useMemo(
    () =>
      STAFF_TEMP_NAV_ITEMS.filter((item) =>
        enabledSchoolTempNavIds.has(item.id),
      ),
    [enabledSchoolTempNavIds],
  );
  const enabledParentTempItems = useMemo(() => {
    if (parentFinancialAidFocusedPortalActive) {
      return [];
    }
    return PARENT_TEMP_NAV_ITEMS.filter((item) =>
      enabledParentTempNavIds.has(item.id),
    );
  }, [enabledParentTempNavIds, parentFinancialAidFocusedPortalActive]);
  const navItems = staffSidebarNavigation.navItems;
  const defaultEntryPath = useMemo(
    () =>
      resolvePersonaDefaultEntry(
        effectivePersona,
        defaultModule.id,
        authorizedSchoolNavIds,
        profile?.groups,
      ),
    [
      authorizedSchoolNavIds,
      defaultModule.id,
      effectivePersona,
      profile?.groups,
    ],
  );
  const activeModule = resolveModuleFromPath(canonicalPath) ?? defaultModule;
  const matchedStaffTempId = resolveStaffTempNavId(canonicalPath);
  const activeStaffTempId = resolveStaffTempNavId(
    canonicalPath,
    enabledStaffTempItems,
  );
  const activeParentTempId = resolveParentTempNavId(
    canonicalPath,
    enabledParentTempItems,
  );
  const activeParentModuleId = resolveParentPortalNavId(canonicalPath);
  const activeParentNavId =
    activeParentTempId ??
    (activeParentModuleId &&
    (activeParentModuleId === "settings" ||
      visibleParentNavIds.has(activeParentModuleId))
      ? activeParentModuleId
      : ((parentNavItems[0]?.id as ParentPortalNavId | undefined) ?? ""));
  const shellTitle = useMemo(() => {
    if (matchedStaffTempId) {
      return (
        STAFF_TEMP_NAV_ITEMS.find((item) => item.id === matchedStaffTempId)
          ?.label ?? activeModule.label
      );
    }
    if (isParentRoute) {
      return "Portal";
    }
    if (isAssistantFullPageRoute) {
      return "Assistant";
    }
    if (isRunReportsRoute) {
      return "Run Reports";
    }
    if (canonicalPath.startsWith("/school/faculty")) {
      return "Faculty";
    }
    if (canonicalPath.startsWith("/school/students")) {
      return "Students & Families";
    }
    return activeModule.label;
  }, [
    activeModule.label,
    canonicalPath,
    isAssistantFullPageRoute,
    isParentRoute,
    isRunReportsRoute,
    matchedStaffTempId,
  ]);
  const isStaffFinancialAidRoute = canonicalPath.startsWith(
    "/school/financialaid",
  );
  const isStudentsFamiliesRoute = canonicalPath.startsWith("/school/students");
  const isAdmissionsDashboardRoute =
    canonicalPath === "/school/admissions/dashboard";
  const isFinancialAidDashboardRoute =
    canonicalPath === "/school/financialaid/dashboard";
  const shouldUseDashboardHeaderContract =
    isAdmissionsDashboardRoute || isFinancialAidDashboardRoute;
  const shellHeaderPolicy = useMemo(
    () =>
      getShellHeaderPolicy({
        pathname: canonicalPath,
        persona: isParentRoute ? "parent" : effectivePersona,
        defaults: {
          showHeaderTitle: true,
          showHeaderActions: isSchoolRoute || isParentRoute,
        },
      }),
    [canonicalPath, effectivePersona, isParentRoute, isSchoolRoute],
  );
  const parentContentInsetProfile =
    resolveParentShellContentInsetProfile(canonicalPath);

  const adminChrome = useAdminChrome();

  const resolveRoleImpersonationEntryPath = useCallback(
    (persona: ShellPersona) =>
      resolvePersonaDefaultEntry(
        persona,
        shellState.defaultModule ?? defaultModule.id,
        enabledSchoolNavIds,
        profile?.groups,
      ),
    [
      defaultModule.id,
      enabledSchoolNavIds,
      profile?.groups,
      shellState.defaultModule,
    ],
  );

  const handleLaunchImpersonationSession = useCallback(
    async (input: LaunchImpersonationSessionInput) => {
      await adminChrome.launchImpersonationSession(input);
      if (!input.targetPersona) {
        return;
      }
      dispatch(setPersonaOverride(input.targetPersona));
      navigate(resolveRoleImpersonationEntryPath(input.targetPersona), {
        replace: true,
      });
    },
    [adminChrome, dispatch, navigate, resolveRoleImpersonationEntryPath],
  );

  const handleClearImpersonationSession = useCallback(async () => {
    await adminChrome.clearImpersonationSession();
    dispatch(setPersonaOverride(null));
    const restoredPersona =
      basePersona === "administrator" || basePersona === "super-administrator"
        ? basePersona
        : effectivePersona;
    navigate(resolveRoleImpersonationEntryPath(restoredPersona), {
      replace: true,
    });
  }, [
    adminChrome,
    basePersona,
    dispatch,
    effectivePersona,
    navigate,
    resolveRoleImpersonationEntryPath,
  ]);

  const adminChromeBanner = useMemo(() => {
    if (!adminChrome.isAdminChrome) return null;
    return (
      <>
        <AdminImpersonationBannerLive
          session={adminChrome.impersonationSession}
          onClearSession={() => {
            void handleClearImpersonationSession();
          }}
        />
        <AdminViewAsBannerLive
          context={adminChrome.viewAsContext}
          onClearContext={() => {
            void adminChrome.clearViewAsContext();
          }}
        />
      </>
    );
  }, [adminChrome, handleClearImpersonationSession]);

  const userCardAction = useMemo(
    () => (
      <SuperAdminImpersonationWidgetLive
        visible={adminChrome.isSuperAdmin}
        activeSession={adminChrome.impersonationSession}
        isLaunching={adminChrome.isLaunchingImpersonation}
        error={adminChrome.impersonationError}
        onLaunchSession={handleLaunchImpersonationSession}
        onClearSession={handleClearImpersonationSession}
      />
    ),
    [
      adminChrome,
      handleClearImpersonationSession,
      handleLaunchImpersonationSession,
    ],
  );

  const headerAdminChrome = null;

  const collapseFullWidthAssistantForRouteTransition = () => {
    if (!resolvedAssistantOpen || resolvedAssistantWidthMode !== "full") {
      return;
    }
    dispatch(setAssistantWidthMode("expanded"));
  };

  const handleNavChange = (id: string) => {
    collapseFullWidthAssistantForRouteTransition();
    const tempRoute =
      enabledStaffTempItems.find((item) => item.id === id)?.route ??
      STAFF_TEMP_ROUTE_MAP.get(id);
    if (tempRoute) {
      window.requestAnimationFrame(() => {
        window.location.assign(tempRoute);
      });
      return;
    }
    const route = staffSidebarNavigation.routeMap.get(id as StaffShellNavId);
    if (!route) return;
    navigate(route);
  };

  const handleParentNavChange = (id: string) => {
    const tempRoute =
      enabledParentTempItems.find((item) => item.id === id)?.route ??
      PARENT_TEMP_ROUTE_MAP.get(id);
    if (tempRoute) {
      window.location.assign(tempRoute);
      return;
    }
    if (id !== "settings" && !visibleParentNavIds.has(id)) {
      return;
    }
    const route = PARENT_PORTAL_ROUTE_MAP[id as ParentPortalNavId];
    if (!route) return;
    navigate(route);
  };

  useEffect(() => {
    if (isParentRoute) {
      return;
    }
    if (isSelfGuardedSchoolRoutePath(canonicalPath)) {
      return;
    }
    if (
      !activeModule.allowedPersonas.includes(effectivePersona as ShellPersona)
    ) {
      navigate(defaultEntryPath, { replace: true });
    }
  }, [
    activeModule,
    canonicalPath,
    defaultEntryPath,
    effectivePersona,
    isParentRoute,
    navigate,
  ]);

  const assistantReturnPath = useMemo(() => {
    const previousPath =
      typeof shellState.lastVisitedPath === "string"
        ? shellState.lastVisitedPath
        : "";
    if (previousPath && !previousPath.startsWith("/school/assistant")) {
      return previousPath;
    }
    return "/school/admissions/dashboard";
  }, [shellState.lastVisitedPath]);

  const handleLogout = async () => {
    const logoutTarget = beginLogoutTransition(POST_LOGOUT_PUBLIC_PATH);
    navigate(logoutTarget, { replace: true });

    try {
      await signOut();
    } catch (error) {
      console.warn(
        "[ShellLayoutRoute] Amplify signOut failed; continuing with local teardown.",
        error,
      );
    } finally {
      dispatch(resetShellState());
      clearShellStorage(profile?.userSub ?? null);
      clearLocalAuthState();
    }
  };

  if (routeNormalizationTarget) {
    return <Navigate to={routeNormalizationTarget} replace />;
  }

  if (staffAccessPendingApproval) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#F8FAF9] px-6 py-16"
        data-testid="staff-access-pending-approval-screen"
        data-extracted-from="owned:ystnj/auth/staff-access-pending-approval"
      >
        <section className="w-full max-w-[620px] rounded-[28px] border border-amber-200 bg-white p-8 text-center shadow-[0_22px_70px_rgba(28,43,38,0.12)]">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
            <Lock className="h-6 w-6 text-amber-700" />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-700">
            Staff access pending
          </p>
          <h1 className="mt-3 font-heading text-[24px] font-semibold text-irongrey">
            Your account is waiting for staff approval
          </h1>
          <p className="mt-3 text-sm leading-6 text-text-secondary">
            You successfully signed in, but staff tools are not available yet. A
            school administrator needs to approve your account, or your email
            domain/sign-up code needs to match the school&apos;s staff access
            settings.
          </p>
          <p className="mt-3 text-sm leading-6 text-text-secondary">
            If you expected immediate access, ask an existing school
            administrator to approve you in Settings &gt; Users or send you an
            active staff sign-up code.
          </p>
          <button
            type="button"
            className="mt-7 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary/90"
            onClick={() => {
              void handleLogout();
            }}
          >
            Sign out
          </button>
        </section>
      </div>
    );
  }

  const isDiscontinuedIntegrationsRoute =
    canonicalPath === "/school/settings/integrations" ||
    canonicalPath.startsWith("/school/settings/integrations/");
  const isSettingsViolation =
    canonicalPath.startsWith("/school/settings") ||
    isSuperAdministratorRoutePath(canonicalPath);
  const settingsViolationTitle = isDiscontinuedIntegrationsRoute
    ? "Legacy route discontinued"
    : isSuperAdministratorRoutePath(canonicalPath)
      ? "Super administrator access required"
      : "Administration access required";
  const settingsViolationBody = isDiscontinuedIntegrationsRoute
    ? "The legacy settings integrations route family has been removed. Use the super-administrator control-plane family for current operator workflows."
    : isSuperAdministratorRoutePath(canonicalPath)
      ? "This area is restricted to super-administrators. You have been redirected to your permitted area."
      : "This area is restricted to school administrators. You have been redirected to your permitted area.";
  const routeViolationNode = routeViolationMessage ? (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-16 text-center"
      data-testid="route-violation-redirect"
      data-testid-alias="settings-denied-shell-state"
      data-extracted-from="owned:ystnj/routing/route-violation"
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Lock className="h-5 w-5 text-text-secondary" />
      </div>
      <h2 className="font-heading text-[18px] font-semibold text-irongrey">
        {isSettingsViolation ? settingsViolationTitle : "Route unavailable"}
      </h2>
      <p className="mt-2 max-w-[360px] text-sm text-text-secondary">
        {isSettingsViolation
          ? settingsViolationBody
          : "This route is not available for your current role. You have been redirected."}
      </p>
    </div>
  ) : null;

  const shouldRenderParentModuleRouteViolationInsideParentShell = Boolean(
    routeViolationMessage &&
      disabledVisibleParentModuleMessage &&
      routeViolationMessage === disabledVisibleParentModuleMessage &&
      parentModuleRouteGuardDisabled,
  );

  if (
    routeViolationMessage &&
    !shouldRenderParentModuleRouteViolationInsideParentShell
  ) {
    return routeViolationNode;
  }

  const assistantFullPageRouteNode = (
    <div
      className="relative flex h-full min-h-full flex-1 flex-col overflow-hidden bg-[#F8FAF9]"
      data-testid="ai-assistant-full-page-host"
      data-extracted-from="owned:ystnj/assistant/full-page-route-host"
    >
      <AdmissionsAIAssistant
        layout="sidebar"
        defaultExpanded
        pathnameOverride={location.pathname}
        searchOverride={location.search}
        onRequestClose={() => navigate(assistantReturnPath)}
        forceSchoolShellDesign
        fullPageAssistant
        widthMode={resolvedAssistantWidthMode}
        onWidthModeChange={(mode) => dispatch(setAssistantWidthMode(mode))}
        routeAssistantContext={activeAssistantRouteContext}
      />
    </div>
  );

  const shellRoutes = (
    <Routes>
      <Route path="/school/assistant/*" element={assistantFullPageRouteNode} />
      <Route
        path="/school/run-reports/*"
        element={
          <RunReportsRoute
            onAssistantRouteContextChange={setAssistantRouteContext}
          />
        }
      />
      <Route
        path="/school/admissions/*"
        element={<AdmissionsSemanticRoute />}
      />
      <Route
        path="/school/attendance/*"
        element={<AttendanceSemanticRoute />}
      />
      <Route
        path="/school/academics/*"
        element={<AcademicsSourceFaithfulRoutes />}
      />
      <Route
        path="/school/substitutes/*"
        element={<SubstituteSourceFaithfulRoutes />}
      />
      <Route
        path="/school/finance/*"
        element={<FinancialOpsSourceFaithfulRoutes />}
      />
      <Route
        path="/school/payroll/*"
        element={<SchoolPayrollSourceFaithfulRoutes />}
      />
      <Route
        path="/school/financialaid/*"
        element={<FinancialAidSemanticRoute />}
      />
      <Route
        path="/school/settings/contracts/date-input-enhancer"
        element={<DateInputEnhancerContractPage />}
      />
      <Route path="/school/settings/*" element={<SettingsSemanticRoute />} />
      <Route
        path="/super-administrator/control-plane/*"
        element={<SuperAdminControlPlaneRoutes />}
      />
      <Route path="/school/faculty/*" element={<FacultySemanticRoute />} />
      <Route
        path="/school/temp/tuition/administrator/*"
        element={
          <TempAliasRedirectRoute
            fromPrefix="/school/temp/tuition/administrator"
            toPrefix="/administrator/tuition"
          />
        }
      />
      <Route
        path="/school/temp/tuition/*"
        element={
          <TempAliasRedirectRoute
            fromPrefix="/school/temp/tuition"
            toPrefix="/school/tuition"
          />
        }
      />
      <Route path="/school/tuition/*" element={<SchoolTuitionOwnedRoutes />} />
      <Route
        path="/administrator/tuition/*"
        element={<AdministratorTuitionInternalRoutes />}
      />
      <Route
        path="/school/camps/*"
        element={<SchoolCampsSourceFaithfulRoutes />}
      />
      <Route
        path="/school/donations/*"
        element={<SchoolDonationsSourceFaithfulRoutes />}
      />
      <Route
        path="/school/tip-a-teacher/*"
        element={<SchoolTipATeacherSourceFaithfulRoutes />}
      />
      <Route
        path="/school/students/*"
        element={<StudentsFamiliesSemanticRoute />}
      />
      <Route
        path="/parent/portal/tip-a-teacher/*"
        element={<ParentTipATeacherSourceFaithfulRoutes />}
      />
      <Route
        path="/parent/portal/temp/tuition/*"
        element={
          <TempAliasRedirectRoute
            fromPrefix="/parent/portal/temp/tuition"
            toPrefix="/parent/portal/tuition"
          />
        }
      />
      <Route
        path="/parent/portal/temp/donations/*"
        element={
          <TempAliasRedirectRoute
            fromPrefix="/parent/portal/temp/donations"
            toPrefix="/parent/portal/donations"
          />
        }
      />
      <Route
        path="/parent/portal/tuition/*"
        element={<ParentTuitionOwnedRoutes />}
      />
      <Route
        path="/parent/portal/donations/*"
        element={<ParentDonationsSourceFaithfulRoutes />}
      />
      <Route
        path="/parent/portal/parnes-hayom/*"
        element={<ParentParnesHayomSourceFaithfulRoutes />}
      />
      <Route
        path="/parent/portal/camps/*"
        element={<ParentCampsSourceFaithfulRoutes />}
      />
      <Route
        path="/parent/portal/academic/*"
        element={<ParentAcademicPerformanceSourceFaithfulRoutes />}
      />
      <Route path="/parent/portal/*" element={<ParentPortalSemanticRoute />} />
      <Route
        path="/parent/portal--new/*"
        element={<ParentPortalAliasRoute />}
      />
      <Route
        path="*"
        element={
          <section data-extracted-from="owned:ystnj/routing/unknown-route">
            Unknown route: {location.pathname}
          </section>
        }
      />
    </Routes>
  );

  const parentPortalUnavailableNode = (
    <section
      className="flex min-h-[55vh] flex-col items-center justify-center px-6 py-12 text-center"
      data-testid="parent-portal-unavailable-shell-notice"
      data-extracted-from="owned:ystnj/parent-portal/unavailable-shell-notice"
    >
      <div className="max-w-[520px] rounded-[24px] border border-border bg-white px-8 py-10 shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Lock className="h-5 w-5 text-text-secondary" />
        </div>
        <h2 className="mt-4 font-heading text-[20px] font-semibold text-irongrey">
          Parent portal unavailable
        </h2>
        <p className="mt-3 text-sm text-text-secondary">
          This school has disabled all parent portal modules. Your session is
          still active, but parent portal navigation is not enabled right now.
        </p>
        <p className="mt-2 text-sm text-text-secondary">
          Contact the school administrator if you expected access, or sign out
          from the account menu when you are done.
        </p>
      </div>
    </section>
  );

  const parentPortalModuleAvailabilityPendingNode = (
    <section
      className="flex min-h-[55vh] flex-col items-center justify-center px-6 py-12 text-center"
      data-testid="parent-portal-module-availability-pending"
      data-extracted-from="owned:ystnj/parent-portal/module-availability-pending"
    >
      <div className="max-w-[520px] rounded-[24px] border border-border bg-white px-8 py-10 shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Lock className="h-5 w-5 text-text-secondary" />
        </div>
        <h2 className="mt-4 font-heading text-[20px] font-semibold text-irongrey">
          Checking parent portal availability
        </h2>
        <p className="mt-3 text-sm text-text-secondary">
          We are confirming which parent portal modules are enabled for this
          family.
        </p>
      </div>
    </section>
  );

  const parentPortalModuleAvailabilityErrorNode = (
    <section
      className="flex min-h-[55vh] flex-col items-center justify-center px-6 py-12 text-center"
      data-testid="parent-portal-module-availability-error"
      data-extracted-from="owned:ystnj/parent-portal/module-availability-error"
    >
      <div className="max-w-[520px] rounded-[24px] border border-border bg-white px-8 py-10 shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Lock className="h-5 w-5 text-text-secondary" />
        </div>
        <h2 className="mt-4 font-heading text-[20px] font-semibold text-irongrey">
          Parent portal availability could not be loaded
        </h2>
        <p className="mt-3 text-sm text-text-secondary">
          We could not confirm which modules are available for this family. Try
          again to reload the school settings.
        </p>
        <button
          type="button"
          className="mt-5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white"
          onClick={retryParentSettings}
        >
          Try again
        </button>
      </div>
    </section>
  );

  const parentFinancialAidAccessGateNode = (
    <section
      className="flex min-h-[55vh] flex-col items-center justify-center px-6 py-12 text-center"
      data-testid="parent-financial-aid-access-gate"
      data-state={parentFinancialAidAccessLookupPending ? "loading" : "blocked"}
    >
      <div className="max-w-[520px] rounded-[24px] border border-border bg-white px-8 py-10 shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Lock className="h-5 w-5 text-text-secondary" />
        </div>
        <h2 className="mt-4 font-heading text-[20px] font-semibold text-irongrey">
          {parentFinancialAidAccessLookupPending
            ? "Checking Financial Aid access"
            : "Financial Aid unavailable"}
        </h2>
        <p className="mt-3 text-sm text-text-secondary">
          {parentFinancialAidAccessLookupPending
            ? "We are confirming whether this family has been invited to complete the Financial Aid application."
            : "The school has not enabled the Financial Aid application for this family yet."}
        </p>
        {!parentFinancialAidAccessLookupPending ? (
          <p className="mt-2 text-sm text-text-secondary">
            Contact the admissions office if you expected access.
          </p>
        ) : null}
      </div>
    </section>
  );

  if (isParentRoute) {
    return (
      <ParentAppShell
        title="Portal"
        subtitle="Parent Portal"
        showHeader
        showHeaderTitle={shellHeaderPolicy.showHeaderTitle}
        showHeaderActions={shellHeaderPolicy.showHeaderActions}
        showAssistant={false}
        showSecondaryNav={
          !zeroEnabledParentModules &&
          !settingsConfigFailed &&
          !parentModuleRouteGuardPending &&
          !parentFinancialAidFocusedPortalActive &&
          enabledParentTempItems.length > 0
        }
        headerPolicyReason={shellHeaderPolicy.reason}
        contentInsetProfile={parentContentInsetProfile}
        navItems={
          zeroEnabledParentModules ||
          settingsConfigFailed ||
          parentModuleRouteGuardPending
            ? []
            : parentNavItems
        }
        navOpen={shellState.navOpen}
        assistantOpen={false}
        secondaryNavItems={
          zeroEnabledParentModules ||
          settingsConfigFailed ||
          parentModuleRouteGuardPending
            ? []
            : enabledParentTempItems
        }
        secondaryNavLabel={TEMP_NAV_LABEL}
        activeNavId={
          zeroEnabledParentModules ||
          settingsConfigFailed ||
          parentModuleRouteGuardPending
            ? ""
            : activeParentNavId
        }
        settingsItem={
          zeroEnabledParentModules ||
          settingsConfigFailed ||
          parentModuleRouteGuardPending ||
          parentFinancialAidFocusedPortalActive
            ? undefined
            : PARENT_PORTAL_SETTINGS_ITEM
        }
        contentVariant="bleed"
        onNavChange={handleParentNavChange}
        onNavOpenChange={(open) => dispatch(setNavOpen(open))}
        onAssistantOpenChange={() => undefined}
        adminChromeBanner={adminChromeBanner}
        headerAdminChrome={headerAdminChrome}
        userCardAction={userCardAction}
        onLogout={handleLogout}
      >
        {settingsConfigFailed
          ? parentPortalModuleAvailabilityErrorNode
          : zeroEnabledParentModules
            ? parentPortalUnavailableNode
            : parentModuleRouteGuardPending
              ? parentPortalModuleAvailabilityPendingNode
              : parentModuleRouteGuardDisabled
                ? routeViolationNode
                : parentFinancialAidAccessGateActive
                  ? parentFinancialAidAccessGateNode
                  : shellRoutes}
      </ParentAppShell>
    );
  }

  return (
    <AppShell
      title={shellTitle}
      showHeader={!isAssistantFullPageRoute}
      showSubtitle={false}
      showHeaderTitle={
        isAssistantFullPageRoute ? false : shellHeaderPolicy.showHeaderTitle
      }
      showHeaderActions={
        isAssistantFullPageRoute ? false : shellHeaderPolicy.showHeaderActions
      }
      assistantDefaultMode={
        shouldDefaultAssistantClosedOnMobileSchool
          ? "closed-mobile-school"
          : "standard"
      }
      assistantOpen={resolvedAssistantOpen}
      assistantResizable={false}
      assistantWidthMode={resolvedAssistantWidthMode}
      lockAssistantOpen={lockAssistantOpen}
      navOpen={shellState.navOpen}
      showTabs={false}
      navItems={navItems}
      secondaryNavItems={enabledStaffTempItems}
      secondaryNavLabel={TEMP_NAV_LABEL}
      settingsItem={staffSidebarNavigation.settingsItem}
      activeNavId={activeStaffTempId ?? staffSidebarNavigation.activeNavId}
      showAssistant={!isAssistantFullPageRoute}
      contentVariant={
        isAssistantFullPageRoute ||
        isRunReportsRoute ||
        isStaffFinancialAidRoute
          ? "bleed"
          : "panel"
      }
      contentInsetMode={
        isAssistantFullPageRoute || isRunReportsRoute ? "none" : "default"
      }
      mainLayoutMode={
        isAssistantFullPageRoute || isRunReportsRoute
          ? "fill-height"
          : "default"
      }
      forceSidebarRailOnCompact={isAssistantFullPageRoute || isRunReportsRoute}
      mainPaddingProfile={
        isStaffFinancialAidRoute ? "school-financial-aid-qa0219" : "default"
      }
      headerClassName={
        shouldUseDashboardHeaderContract ? "space-y-4 pb-4" : undefined
      }
      forceHeaderMarginBottomZero={
        isRunReportsRoute ||
        isStudentsFamiliesRoute ||
        shouldUseDashboardHeaderContract
      }
      forceMainMarginRightZero={isRunReportsRoute || isStudentsFamiliesRoute}
      assistant={
        <AdmissionsAIAssistant
          layout="sidebar"
          defaultExpanded={resolvedAssistantOpen}
          pathnameOverride={location.pathname}
          searchOverride={location.search}
          onRequestClose={
            lockAssistantOpen
              ? undefined
              : () => dispatch(setAssistantOpen(false))
          }
          forceSchoolShellDesign={shellHeaderPolicy.showHeaderActions}
          widthMode={resolvedAssistantWidthMode}
          onWidthModeChange={(mode) => dispatch(setAssistantWidthMode(mode))}
          routeAssistantContext={activeAssistantRouteContext}
        />
      }
      onNavChange={handleNavChange}
      onNavOpenChange={(open) => dispatch(setNavOpen(open))}
      onAssistantOpenChange={(open) => dispatch(setAssistantOpen(open))}
      onAssistantWidthModeChange={(mode) =>
        dispatch(setAssistantWidthMode(mode))
      }
      adminChromeBanner={adminChromeBanner}
      headerAdminChrome={headerAdminChrome}
      userCardAction={userCardAction}
      onLogout={handleLogout}
    >
      {shellRoutes}
    </AppShell>
  );
};
